import { StaffMember } from '../types';

/**
 * Normaliza string para comparação sem acentos, pontuação e caixa baixa.
 */
export function normalizeServiceKey(str?: string): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Extrai a lista de IDs de serviços atendidos pelo profissional,
 * com fallback duplo para permissions.serviceIds.
 */
export function getEffectiveStaffServiceIds(staff: StaffMember): string[] {
  if (Array.isArray(staff.serviceIds) && staff.serviceIds.length > 0) {
    return staff.serviceIds;
  }
  const permServices = (staff.permissions as any)?.serviceIds;
  if (Array.isArray(permServices) && permServices.length > 0) {
    return permServices;
  }
  return [];
}

/**
 * Verifica se um profissional da equipe está habilitado a realizar um determinado serviço.
 * Suporta tanto vínculo bidirecional (staff.serviceIds quanto service.allowedStaffIds)
 * com correspondência estrita quando houver especialidades configuradas.
 */
export function isStaffEligibleForService(
  staff: StaffMember,
  service: { id: string; name?: string; allowedStaffIds?: string[] },
  catalogServices?: Array<{ id: string; name: string }>
): boolean {
  // 'st_any' é o coringa de "Qualquer Profissional"
  if (staff.id === 'st_any') return true;

  const effectiveServiceIds = getEffectiveStaffServiceIds(staff);

  // 1. Se o profissional possui lista explícita de serviços atendidos
  if (effectiveServiceIds.length > 0) {
    // 1.1 Match direto por ID
    if (effectiveServiceIds.includes(service.id)) {
      return true;
    }

    // 1.2 Match híbrido por Nome / Chave Normalizada (caso IDs divirjam entre front e banco)
    if (service.name) {
      const normTargetName = normalizeServiceKey(service.name);

      // Se foi fornecido catálogo completo, resolve os nomes reais configurados no staff
      if (catalogServices && catalogServices.length > 0) {
        const staffAllowedNames = catalogServices
          .filter(cs => effectiveServiceIds.includes(cs.id))
          .map(cs => normalizeServiceKey(cs.name));
        if (staffAllowedNames.includes(normTargetName)) {
          return true;
        }
      }

      // Fallback: se os serviceIds contiverem parte da palavra (ex: 's_corte' -> 'corte' vs 'corte de cabelo')
      const matchesIdPattern = effectiveServiceIds.some(sid => {
        const cleanSid = sid.replace(/^(s_|cs_|srv_|p_|item_)/i, '');
        const normSid = normalizeServiceKey(cleanSid);
        if (normSid.length < 3) return false;
        // Match exato ou casamento com palavra individual do nome do serviço
        if (normTargetName === normSid) return true;
        const targetWords = (service.name || '').toLowerCase().split(/[\s_-]+/);
        return targetWords.some(w => normalizeServiceKey(w) === normSid);
      });
      if (matchesIdPattern) {
        return true;
      }
    }

    // Se o profissional tem lista definida e o serviço NÃO está nela, é ESTRITAMENTE FALSO
    return false;
  }

  // 2. Se o serviço possui lista explícita de profissionais habilitados
  if (service.allowedStaffIds && service.allowedStaffIds.length > 0) {
    return service.allowedStaffIds.includes(staff.id);
  }

  // 3. Fallback retrocompatível: se não houver restrições cadastradas em nenhum dos lados
  return true;
}

/**
 * Filtra a lista de profissionais da equipe que estão habilitados para TODOS os serviços selecionados.
 */
export function filterStaffForServices(
  staffList: StaffMember[],
  selectedServices: Array<{ id: string; name?: string; allowedStaffIds?: string[] }>,
  catalogServices?: Array<{ id: string; name: string }>
): StaffMember[] {
  if (selectedServices.length === 0) return staffList;
  if (staffList.length === 0) return [];

  return staffList.filter(st => {
    if (st.id === 'st_any') return true;
    return selectedServices.every(srv => isStaffEligibleForService(st, srv, catalogServices));
  });
}

/**
 * Retorna os nomes ou títulos dos serviços atendidos por um profissional.
 */
export function getStaffServiceNames(
  staff: StaffMember,
  allServices: Array<{ id: string; name: string }>
): string[] {
  const effectiveIds = getEffectiveStaffServiceIds(staff);
  if (effectiveIds.length === 0) {
    return [];
  }
  return allServices
    .filter(s => effectiveIds.includes(s.id) || effectiveIds.some(eid => normalizeServiceKey(eid) === normalizeServiceKey(s.name)))
    .map(s => s.name);
}
